import './protection.scss';
import React, { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useNavigate } from 'src/utils/use-navigate';
import { observer } from 'mobx-react-lite';
import { MainPages, ProtectionMenuNameOptions } from '../../../utils/enums';
import { useStores } from 'src/components/store';
import { HeaderRightBar } from './protection-header/header-right-bar';
import { NavigationMenu } from './navigation-menu';
import { protectionNavigationMenuOptions } from './navigation-menu-options';
import usePromiseFeedback from 'src/components/shared/promise-feedback/usePromiseFeedback';

export const Protection: React.FC = observer(() => {
    const [hasFatalError, setHasFatalError] = useState(false);
    const { fwVersionStore, uiStore, customerStore } = useStores();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();

    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        uiStore.setMainPage(MainPages.POLICY);
    }, []);

    useEffect(() => {
        return () => {
            fwVersionStore.setSavingStatus(undefined);
        };
    }, []);

    useEffect(() => {
        if(!params.versionId || !fwVersionStore.activeVersionData) return;
        if (params.versionId !== fwVersionStore.selectedFwVersion?.externalId) {
            if (params.versionId === fwVersionStore.activeVersionData.externalId) {
                fwVersionStore.copyActiveVersionToSelectedVersion();
            } else {
                promiseFeedbackFactory({
                    promise: fwVersionStore.loadVersionData(params.versionId),
                    notifObj: {show: false},
                    successCallback: () => setHasFatalError(false),
                    errorCallback: () => setHasFatalError(true)
                });
            }
        }
    }, [params.versionId, fwVersionStore.activeVersionData]);

    useEffect(() => {
        if (!params.versionId || !fwVersionStore.fwVersions?.length) return;
        if (!fwVersionStore.fwVersions.some(version => version.externalId === params.versionId)) {
            const newVersionId = fwVersionStore.draftVersion?.externalId ?? fwVersionStore.getActiveVersion?.externalId ?? fwVersionStore.selectedVersionId;
            if (newVersionId && newVersionId !== params.versionId) {
                navigate(window.location.pathname.replace(`/version/${params.versionId}`, `/version/${newVersionId}`), { replace: true });
                setHasFatalError(false);
            }
        }
    }, [params.versionId, fwVersionStore.fwVersions]);

    useEffect(() => {
        if (fwVersionStore.selectedFwVersion && params.versionId && params.versionId !== fwVersionStore.selectedFwVersion?.externalId) {
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(`/version/${params.versionId}`, `/version/${fwVersionStore.selectedFwVersion.externalId}`);
            navigate(newPath, { replace: true });
        }
    }, [fwVersionStore.selectedFwVersion]);

    const calculatePath = (type: string) => {
        const subPath = fwVersionStore.calculateSubProtectionPath(type as ProtectionMenuNameOptions);
        return `/${customerStore.customerId}/${customerStore.serviceId}/protection/version/${fwVersionStore.selectedVersionId}/${type}${subPath}`;
    };

    return <div className="protection-page">
        <div className="page-header" data-testid="page-header">
            <NavigationMenu calculatePath={calculatePath}
                activeKey={fwVersionStore.protectionType}
                navigationMenuOptions={protectionNavigationMenuOptions} />
            <HeaderRightBar />
        </div>
        <div className={`protection-content ${fwVersionStore.savingStatus?.toLowerCase()} ${hasFatalError ? 'fatal-error' : ''}`}>
            <Outlet />
        </div>
        {promiseContextHolder}
    </div>;
});
